import { AssessmentStatus } from 'fhir/QuestionnaireResponse/constants'

export const fuseOptionKeys = [
  'name',
  'source.display',
  'author.display',
  'subject.display',
  'jurisdiction.coding.display'
]

export const routeMyAssessments = '/clinical/assessments/my-assessments'
export const routeAllAssessments = '/clinical/assessments/all-assessments'

export enum AssessmentType {
  SOC = 'soc',
  PAT = 'pat',
  RECERT = 'recert',
  FUA = 'fua',
  SUPERVISORY = 'supervisory',
  DISCHARGE = 'discharge',
  ROC = 'roc',
  SERVICE_PLAN = 'service-plan'
}

export enum AssessmentVariant {
  SOC = 'soc',
  PAT = 'pat',
  RECERT = 'recert',
  FUA = 'fua',
  FUA_REVISION_TO_POC = 'fua-revision-to-poc',
  FUA_CHANGE_IN_CONDITION = 'fua-change-in-condition',
  FUA_REAUTHORIZATION = 'fua-reauthorization',
  FUA_CREATE_ACUTE_PERIOD = 'fua-create-acute-period',
  FUA_UPDATE_ACUTE_PERIOD = 'fua-update-acute-period',
  SUPERVISORY = 'supervisory',
  DISCHARGE = 'discharge',
  ROC = 'roc',
  SERVICE_PLAN = 'service-plan'
}

export const MODAL_ASSESSMENT_TO_QUESTIONNAIRE_MAP: Record<AssessmentVariant, AssessmentType> = {
  [AssessmentVariant.SOC]: AssessmentType.SOC,
  [AssessmentVariant.PAT]: AssessmentType.PAT,
  [AssessmentVariant.RECERT]: AssessmentType.RECERT,
  [AssessmentVariant.FUA]: AssessmentType.FUA,
  [AssessmentVariant.FUA_REVISION_TO_POC]: AssessmentType.FUA,
  [AssessmentVariant.FUA_CHANGE_IN_CONDITION]: AssessmentType.FUA,
  [AssessmentVariant.FUA_REAUTHORIZATION]: AssessmentType.FUA,
  [AssessmentVariant.FUA_CREATE_ACUTE_PERIOD]: AssessmentType.FUA,
  [AssessmentVariant.FUA_UPDATE_ACUTE_PERIOD]: AssessmentType.FUA,
  [AssessmentVariant.SUPERVISORY]: AssessmentType.SUPERVISORY,
  [AssessmentVariant.DISCHARGE]: AssessmentType.DISCHARGE,
  [AssessmentVariant.ROC]: AssessmentType.ROC,
  [AssessmentVariant.SERVICE_PLAN]: AssessmentType.SERVICE_PLAN
}

export const getAssessmentTypeFromModal = (modalType: AssessmentVariant): AssessmentType => {
  return MODAL_ASSESSMENT_TO_QUESTIONNAIRE_MAP[modalType]
}

export const CARE_SKILLED_ASSESSMENT_TYPES = [
  AssessmentType.SOC,
  AssessmentType.RECERT,
  AssessmentType.FUA,
  AssessmentType.DISCHARGE,
  AssessmentType.ROC
]

export const ASSESSMENTS_WITH_GENERATED_RESOURCES = [
  AssessmentType.SOC,
  AssessmentType.RECERT,
  AssessmentType.FUA,
  AssessmentType.DISCHARGE,
  AssessmentType.ROC,
  AssessmentType.SERVICE_PLAN
]

export const ASSESSMENTS_WITH_MANUAL_RESOURCE_GENERATION = [
  AssessmentType.SOC,
  AssessmentType.RECERT,
  AssessmentType.FUA,
  AssessmentType.ROC,
  AssessmentType.SERVICE_PLAN
]

export const ASSESSMENTS_WITH_PLOT_VISITS = [AssessmentType.SOC, AssessmentType.RECERT]

export const UNSKILLED_ASSESSMENT_TYPES = [AssessmentType.SERVICE_PLAN]

export enum AssessmentCategory {
  SKILLED = 'skilled',
  UNSKILLED = 'unskilled',
  ALL = 'all'
}

export const getAssessmentCategory = (assessmentType?: AssessmentType): AssessmentCategory => {
  if (!assessmentType) {
    return AssessmentCategory.ALL
  }
  if (UNSKILLED_ASSESSMENT_TYPES.includes(assessmentType)) {
    return AssessmentCategory.UNSKILLED
  }
  if (CARE_SKILLED_ASSESSMENT_TYPES.includes(assessmentType)) {
    return AssessmentCategory.SKILLED
  }
  return AssessmentCategory.ALL
}

export const CareAssessmentTypes = [
  AssessmentType.SOC,
  AssessmentType.SERVICE_PLAN,
  AssessmentType.RECERT,
  AssessmentType.FUA,
  AssessmentType.DISCHARGE,
  AssessmentType.ROC
]

export const GENERATE_PDF_ASSESSMENT_TYPES = [
  AssessmentType.SOC,
  AssessmentType.RECERT,
  AssessmentType.DISCHARGE
]

export const AssessmentTypeLabel = {
  [AssessmentType.SOC]: 'Start of Care',
  [AssessmentType.PAT]: 'Skilled Pediatric Assessment - Colorado',
  [AssessmentType.RECERT]: 'Recertification',
  [AssessmentType.FUA]: 'Follow-Up',
  [AssessmentVariant.FUA_REVISION_TO_POC]: 'Follow-Up (Revision to POC)',
  [AssessmentVariant.FUA_CHANGE_IN_CONDITION]: 'Follow-Up (Change in Condition)',
  [AssessmentVariant.FUA_REAUTHORIZATION]: 'Follow-Up (Reauthorization)',
  [AssessmentVariant.FUA_CREATE_ACUTE_PERIOD]: 'Follow-Up (Create an Acute Period)',
  [AssessmentVariant.FUA_UPDATE_ACUTE_PERIOD]: 'Follow-Up (Update an Acute Period)',
  [AssessmentType.SUPERVISORY]: 'Supervisory',
  [AssessmentType.DISCHARGE]: 'Discharge',
  [AssessmentType.ROC]: 'Resumption of Care',
  [AssessmentType.SERVICE_PLAN]: 'Service Plan'
}
export const AssessmentStatusLabel = {
  [AssessmentStatus.IN_PROGRESS]: 'Draft',
  [AssessmentStatus.PENDING_QA]: 'Pending QA',
  [AssessmentStatus.PENDING_QA_EDITS]: 'Corrections Needed',
  [AssessmentStatus.PENDING_APPROVAL]: 'Pending Approval',
  [AssessmentStatus.COMPLETED]: 'Completed',
  [AssessmentStatus.ENTERED_IN_ERROR]: 'Deleted'
}

export const statusColorMap = {
  draft: { bg: '#F4F3FF', text: '#5925DC' },
  'pending qa': { bg: '#FFFAEB', text: '#B54708' },
  'corrections needed': { bg: '#FDF4FF', text: '#9000A3' },
  'pending approval': { bg: '#FDF4FF', text: '#9000A3' },
  completed: { bg: '#ECFDF3' },
  deleted: { bg: '#FEF3F2', text: '#B42318' }
}

export const VALIDATABLE_ASSESSMENT_TYPES = [
  AssessmentType.SOC,
  AssessmentType.RECERT,
  AssessmentType.FUA,
  AssessmentType.ROC,
  AssessmentType.DISCHARGE,
  AssessmentType.SERVICE_PLAN
]

export interface AssessmentTypeConfig {
  value: AssessmentVariant
  label: string
  briefDescription: string
  detailedDescription: string
}

export const ASSESSMENT_TYPE_CONFIGS: AssessmentTypeConfig[] = [
  {
    value: AssessmentVariant.SOC,
    label: AssessmentTypeLabel[AssessmentVariant.SOC],
    briefDescription: 'Initial assessment for new patients beginning home health services',
    detailedDescription:
      'This assessment should be used when admitting a new patient to services and to create their initial plan of care. It generates a CMS-485 and creates patient shifts to allow caregivers to chart.'
  },
  {
    value: AssessmentVariant.PAT,
    label: AssessmentTypeLabel[AssessmentVariant.PAT],
    briefDescription: 'Pediatric assessment to assess patient needs in the state of Colorado',
    detailedDescription:
      'This assessment allows you to create a scoring assessment for patients seeking home health services in Colorado. This assessment creates an official PAT document stored in the patient chart.'
  },
  {
    value: AssessmentVariant.RECERT,
    label: AssessmentTypeLabel[AssessmentVariant.RECERT],
    briefDescription: 'Periodic reassessment to continue home health services',
    detailedDescription:
      'This assessment recertifies a patient for services. It generates a new CMS-485 and allows you to update the comprehensive assessment, plan of care, and schedule.'
  },
  {
    value: AssessmentVariant.FUA_REVISION_TO_POC,
    label: AssessmentTypeLabel[AssessmentVariant.FUA_REVISION_TO_POC],
    briefDescription: 'Follow-up assessment to make permanent updates to patient interventions',
    detailedDescription:
      'This assessment allows you to make permanent updates to patient interventions in an existing episode of care. This assessment does not generate a CMS-485 or allow you to change the frequency of patient services.'
  },

  {
    value: AssessmentVariant.FUA_CHANGE_IN_CONDITION,
    label: AssessmentTypeLabel[AssessmentVariant.FUA_CHANGE_IN_CONDITION],
    briefDescription: 'Follow-up assessment to document changes in patient medical condition',
    detailedDescription:
      "This assessment allows you to document changes on a patient's medical condition. This assessment does not allow you to edit patient interventions, authorization, or patient schedule."
  },

  {
    value: AssessmentVariant.FUA_REAUTHORIZATION,
    label: AssessmentTypeLabel[AssessmentVariant.FUA_REAUTHORIZATION],
    briefDescription: 'Follow-up assessment to renew services when new authorization is provided',
    detailedDescription:
      'This assessment allows you to renew services when new authorization is provided by a payor. This assessment does not generate a CMS-485, though allows you to modify the patient interventions or schedule.'
  },

  {
    value: AssessmentVariant.FUA_CREATE_ACUTE_PERIOD,
    label: AssessmentTypeLabel[AssessmentVariant.FUA_CREATE_ACUTE_PERIOD],
    briefDescription:
      'Follow-up assessment to create a temporary care plan with increased services',
    detailedDescription:
      'This assessment allows you to create a temporary care plan with increased services due to a severe change in patient needs. This assessment does not allow you to change permanent patient interventions or generate a CMS-485.'
  },

  {
    value: AssessmentVariant.FUA_UPDATE_ACUTE_PERIOD,
    label: AssessmentTypeLabel[AssessmentVariant.FUA_UPDATE_ACUTE_PERIOD],
    briefDescription: 'Follow-up assessment to end an existing acute period earlier than scheduled',
    detailedDescription:
      'This assessment allows you to end an existing acute period earlier than previously scheduled. This assessment does not generate a CMS-485 or allow you to change the patient interventions or schedule.'
  },
  {
    value: AssessmentVariant.SUPERVISORY,
    label: AssessmentTypeLabel[AssessmentVariant.SUPERVISORY],
    briefDescription: 'Supervisory assessment for quality assurance and oversight',
    detailedDescription:
      'This assessment allows you to document oversight of caregiver services and competencies. This assessment does not allow you to edit patient interventions, authorization, or patient schedule.'
  },
  {
    value: AssessmentVariant.DISCHARGE,
    label: AssessmentTypeLabel[AssessmentVariant.DISCHARGE],
    briefDescription: 'Final assessment when patient completes home health services',
    detailedDescription:
      'This assessment allows you to formally discharge a patient from home health services. This assessment does not allow you to update patient interventions or schedule.'
  },
  {
    value: AssessmentVariant.ROC,
    label: AssessmentTypeLabel[AssessmentVariant.ROC],
    briefDescription: 'Assessment when resuming care after a temporary interruption',
    detailedDescription:
      "This assessment allows you to resume a patient's services after returns from a hospital stay or other care interruption. This assessment does not generate a CMS-485 or allow you to change the frequency of patient services."
  },
  {
    value: AssessmentVariant.SERVICE_PLAN,
    label: AssessmentTypeLabel[AssessmentVariant.SERVICE_PLAN],
    briefDescription: 'Service plan assessment for care coordination and planning',
    detailedDescription:
      'This assessment allows you to create a care plan for a patient with personal care services. This creates patient shifts to allow caregivers to chart.'
  }
]

export enum CreateAssessmentModalStep {
  SELECT = 0,
  CONFIRM = 1,
  CREATE = 2
}

export const ASSESSMENTS_WITH_485_REVIEW_REQUIRED = [AssessmentType.SOC, AssessmentType.RECERT]
